<template>
    <div>
        <h2>Orders</h2>
        <div class="sync-buttons">
            <button @click="fetchData('/orders', 1)">Refresh Orders</button>
            <button @click="syncData('/orders/sync')">Sync Orders</button>
            <button @click="ProcessOrders">Sync with QBO</button>
        </div>
        <div class="items-per-page">
            <span>Items per page:</span>
            <select v-model="itemsPerPage" @change="fetchData('/orders', 1)">
                <option value="10">10</option>
                <option value="50">50</option>
                <option value="250">250</option>
            </select>
        </div>
        <div v-if="loading" class="loading-spinner">
            Loading data...
        </div>
        <div v-else>
            <div v-if="totalItems > 0">
                <p>Total Orders: {{ totalItems }}</p>
            </div>
            <table v-if="totalItems > 0">
                <thead>
                    <tr>
                        <th v-for="(header, index) in headers" :key="index" @click="sortTable(header)">
                            {{ formatHeader(header) }}
                            <span v-if="sortColumn === header && sortDirection === 'asc'">▲</span>
                            <span v-if="sortColumn === header && sortDirection === 'desc'">▼</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(order, index) in orders" :key="index">
                        <td v-for="(header, index) in headers" :key="index">
                            <span v-if="header === 'createdAt'">{{ formatDate(order.node[header]) }}</span>
                            <span v-else-if="header === 'totalPrice'">{{ formatCurrency(order.node.currentTotalPriceSet.shopMoney.amount) }}</span>
                            <span v-else-if="header === 'isCancelled'">{{ order.node[header] !== null ? formatDate(order.node[header]) : "-" }}</span>
                            <span v-else-if="header === 'Order'">{{ (order.node.name) }}</span>
                            <span v-else-if="header === 'preorder'">{{ IsPreOrder(order) === true ? '✅' : '𐄂' }}</span>
                            <span v-else-if="header === 'syncedWithQBO'">{{ order[header] === true ? '✅' : '𐄂' }}</span>
                            <span v-else>{{ order.node[header] }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-else>
                <p>No orders available.</p>
            </div>
            <div class="pagination" v-if="totalItems > 0">
                <button :disabled="currentPage === 1" @click="fetchData('/orders', currentPage - 1)">
                    Previous
                </button>
                <span>Page {{ currentPage }} of {{ totalPages }}</span>
                <button :disabled="currentPage === totalPages" @click="fetchData('/orders', currentPage + 1)">
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { useErrorHandling } from '../composables/errorHandling';
import { useToken } from '../composables/useToken';
import { formatDate, formatCurrency } from '../utils/formatters';

const { handleApiError } = useErrorHandling();
const { GetToken } = useToken();

const orders = ref([]);
const loading = ref(false);
const headers = ref(['name','createdAt','totalPrice','cancelledAt', 'displayFulfillmentStatus', 'displayFinancialStatus', 'preorder', 'syncedWithQBO']);
const sortColumn = ref('');
const sortDirection = ref('asc');
const itemsPerPage = ref(10);
const currentPage = ref(1);
const totalItems = ref(0);
const totalPages = ref(1);

const fetchData = async (url, page = 1) => {
    loading.value = true;

    const data = {
        SessionToken: GetToken(),
        ItemsPerPage: itemsPerPage.value,
        Page: page,
    };
    
    const endpoint = `${process.env.VUE_APP_BASE_URL}${url}`;
    
    try
    {
        const response = await axios.post(endpoint, data)
        .then(response => {
            return response.data;
        })
        .catch(error => {
            handleApiError(error);
        });

        console.log(response);

        // Format currentTotalPriceSet.shopMoney.amount to number
        for (let i = 0; i < response.data.length; i++)
        {
            response.data[i].node.currentTotalPriceSet.shopMoney.amount = parseFloat(response.data[i].node.currentTotalPriceSet.shopMoney.amount);
        }

        orders.value.length = 0;
        orders.value = response.data;
        console.log('orders', orders.value);
        // itemsPerPage.value = response.ItemsPerPage;
        currentPage.value = response.Page;
        totalItems.value = response.Total;
        totalPages.value = Math.ceil(totalItems.value / itemsPerPage.value);
    }
    catch (error)
    {
        handleApiError(error.message);
    }
    finally
    {
        loading.value = false;
    }
};

const syncData = async (url) => {
    loading.value = true;
    
    const data = {
        SessionToken: GetToken(),
        ItemsPerPage: itemsPerPage.value,
        Page: currentPage.value,
    };
    
    const endpoint = `${process.env.VUE_APP_BASE_URL}${url}`;
    console.log('sync orders request', data);
    try
    {
        const response = await axios.post(endpoint, data)
        .then(response => {
            return response.data;
        })
        .catch(error => {
            handleApiError(error);
        });

        console.log('orders sync response', response);
        
        // Format currentTotalPriceSet.shopMoney.amount to number
        for (let i = 0; i < response.data.length; i++)
        {
            response.data[i].node.currentTotalPriceSet.shopMoney.amount = parseFloat(response.data[i].node.currentTotalPriceSet.shopMoney.amount);
        }

        orders.value.length = 0;
        orders.value = response.data;
        // itemsPerPage.value = response.ItemsPerPage;
        currentPage.value = response.Page;
        totalItems.value = response.Total;
        totalPages.value = Math.ceil(totalItems.value / itemsPerPage.value);
    }
    catch (error)
    {
        handleApiError(error.message);
    }
    finally
    {
        loading.value = false;
    }
};

const ProcessOrders = async () => {

    const data = {
        SessionToken: GetToken(),
    };

    const endpoint = `${process.env.VUE_APP_BASE_URL}/sync`;

    const result = await axios.post(endpoint, data)
    .then(response => {
        return response.data;
    })
    .catch(error => {
        console.log('Sync with QBO error: ', error);
        return null;
    });

    console.log('QBO sync result', result);
};

const sortTable = (header) => {
  if (sortColumn.value === header)
  {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  }
  else
  {
    sortColumn.value = header;
    sortDirection.value = 'asc';
  }
  
  orders.value.sort((a, b) => {

    let aValue = 0;
    let bValue = 0;

    if (header === 'totalPrice')
    {
        aValue = a.node.currentTotalPriceSet.shopMoney.amount;
        bValue = b.node.currentTotalPriceSet.shopMoney.amount;
    }
    else
    {
        aValue = a.node[header];
        bValue = b.node[header];
    }
    
    
    if (aValue < bValue)
    {
      return sortDirection.value === 'asc' ? -1 : 1;
    }

    if (aValue > bValue)
    {
      return sortDirection.value === 'asc' ? 1 : -1;
    }

    return 0;
  });
};

const formatHeader = (header) => {
    switch (header)
    {
        case 'name': return 'Order';
        case 'createdAt': return 'Created At';
        case 'totalPrice': return 'Total Price';
        case 'cancelledAt': return 'Cancelled';
        case 'displayFulfillmentStatus': return 'Fulfillment';
        case 'displayFinancialStatus': return 'Payment';
        case 'preorder': return 'Pre-Order';
        case 'syncedWithQBO': return 'Synced';

        default: return header;
    }
};

const IsPreOrder = (order) => {
    if (order.node.lineItems.edges.length > 0)
    {
        if (order.node.lineItems.edges[0].node.product !== null &&
            order.node.lineItems.edges[0].node.product.metafield !== null)
        {
            return true;
        }
    }

    return false;
};

onMounted(() => {
  fetchData('/orders', 1);
});
</script>

<style scoped>
.sync-buttons {
  margin-bottom: 10px;
}
.loading-spinner {
  text-align: center;
  margin-top: 20px;
}
table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 20px;
    margin-bottom: 20px;
  }
  th, td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
  }
  th {
    background-color: #f2f2f2;
    cursor: pointer;
  }
  th:hover {
      background-color: #e0e0e0;
  }

.pagination {
    margin-top: 20px;
    text-align: center;
}

.pagination button {
  margin: 0 5px;
  padding: 5px 10px;
  border: 1px solid #ddd;
  background-color: white;
  cursor: pointer;
}

.pagination button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.items-per-page {
    margin-bottom: 10px;
    display: flex;
    align-items: center;
}

.items-per-page span {
    margin-right: 5px;
}
</style>